import { ChevronDown } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { ICP_RATINGS, type IcpJevItem, type IcpRating } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Popover } from '../../../components/Popover';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { LINE_RATING_WORDS, type LineFilters } from '../line-filters';
import styles from './ranking.module.css';

// ADR 0068: a button in the filter bar for each profile line Jev reads. The first press turns the filter on at the line's rating in
// the job; pressing it again opens a list to change that rating. It narrows the list only: no order, score or tab moves.

/** How long after the pop-up closed on a press the same button's click still counts as part of that press. */
const JUST_CLOSED_MS = 400;

/** Highest first, the order the ideal candidate section lists them in. */
const RATINGS_HIGH_TO_LOW: readonly IcpRating[] = [...ICP_RATINGS].reverse();

interface LineFilterButtonsProps {
  readonly lines: readonly IcpJevItem[];
  /** The profile's last saved rating for each line: "the job's rating" (the Focus bar may have changed the ones on screen). */
  readonly jobRatings: Readonly<Record<string, IcpRating>>;
  readonly filters: LineFilters;
  readonly onChange: (filters: LineFilters) => void;
}

export function LineFilterButtons({ lines, jobRatings, filters, onChange }: LineFilterButtonsProps): ReactNode {
  const [open, setOpen] = useState<{ readonly id: string; readonly anchor: AnchorRect } | null>(null);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  // A press on the button is "outside" its own pop-up, so the pop-up closes on pointer-down (before this button's own handlers run);
  // the click that follows a moment later must not open it again.
  const justClosed = useRef<string | null>(null);

  const jobRatingOf = (line: IcpJevItem): IcpRating => jobRatings[line.id] ?? line.rating;
  const without = (id: string): LineFilters => Object.fromEntries(Object.entries(filters).filter(([key]) => key !== id));

  /** `byOutsidePress`: the pop-up closed itself on a press or Esc, which a press on this very button may be about to repeat as a click. */
  function close(byOutsidePress: boolean): void {
    const id = open?.id;
    setOpen(null);
    if (id === undefined) return;
    if (byOutsidePress) {
      justClosed.current = id;
      window.setTimeout(() => {
        if (justClosed.current === id) justClosed.current = null;
      }, JUST_CLOSED_MS);
    }
    buttons.current.get(id)?.focus();
  }

  function press(line: IcpJevItem, button: HTMLButtonElement): void {
    if (filters[line.id] === undefined) {
      onChange({ ...filters, [line.id]: jobRatingOf(line) });
      return;
    }
    if (justClosed.current === line.id) {
      justClosed.current = null;
      return;
    }
    if (open?.id === line.id) {
      close(false);
      return;
    }
    setOpen({ id: line.id, anchor: anchorFrom(button) });
  }

  const openLine = open ? lines.find((line) => line.id === open.id) : undefined;
  const openRating = openLine ? filters[openLine.id] : undefined;

  return (
    <>
      <span className={styles.lineFilters} role="group" aria-label="Lines of the ideal candidate">
        {lines.map((line) => {
          const rating = filters[line.id];
          const isOn = rating !== undefined;
          return (
            <button
              key={line.id}
              ref={(node) => {
                if (node) buttons.current.set(line.id, node);
                else buttons.current.delete(line.id);
              }}
              type="button"
              className={styles.filterButton}
              data-active={isOn || undefined}
              title={isOn ? `${line.text}: ${LINE_RATING_WORDS[rating]}. Press to change the rating.` : `Show people Jev found ${line.text} for, at the job's rating of ${jobRatingOf(line)}`}
              aria-haspopup={isOn ? 'dialog' : undefined}
              aria-expanded={isOn ? open?.id === line.id : undefined}
              aria-pressed={isOn ? undefined : false}
              onClick={(event) => press(line, event.currentTarget)}
            >
              <span className={styles.lineFilterLabel}>{line.text}</span>
              {isOn && <span className={styles.filterValue}>{rating}</span>}
              {isOn && <ChevronDown size={14} aria-hidden />}
            </button>
          );
        })}
      </span>
      <Popover anchor={open?.anchor ?? null} onClose={() => close(true)} label={openLine ? `${openLine.text} filter` : 'Line filter'} align="left">
        {openLine && openRating !== undefined && (
          <div className={styles.lineFilterPanel}>
            <span className={styles.filterPanelTitle}>{openLine.text}</span>
            <span className={styles.lineFilterHint}>Show people Jev found at this level or better. People Jev hasn’t read are hidden while a line filter is on.</span>
            <div role="radiogroup" aria-label={`Rating for ${openLine.text}`} className={styles.lineOptions}>
              {RATINGS_HIGH_TO_LOW.map((candidate) => (
                <button key={candidate} type="button" role="radio" aria-checked={candidate === openRating} className={styles.lineOption} onClick={() => onChange({ ...filters, [openLine.id]: candidate })}>
                  <span className={styles.lineRadio} aria-hidden />
                  <span className={styles.lineRatingNumber}>{candidate}</span>
                  {LINE_RATING_WORDS[candidate]}
                  {candidate === jobRatingOf(openLine) && <span className={styles.lineJobTag}>job’s rating</span>}
                </button>
              ))}
            </div>
            {openRating !== jobRatingOf(openLine) && (
              <Button size="sm" variant="ghost" onClick={() => onChange({ ...filters, [openLine.id]: jobRatingOf(openLine) })}>
                Back to the job’s rating ({jobRatingOf(openLine)})
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onChange(without(openLine.id));
                close(false);
              }}
            >
              Turn off this filter
            </Button>
          </div>
        )}
      </Popover>
    </>
  );
}
